import React, { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'
import { GearIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { formatDateLong } from '@/utils/date'
import { popupFactory } from '@/factories'
import { ResidentVehiclesModal } from '@/pages/parking-management/residents-parking/parking-modals'

import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import Filter from '../../Filter/Filter'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import DateField from '../../FormLegacy/FormFieldsV2/DateField'
import Icon from '../../Icons'
import API from '../../../api/api'
import { getPropertyOptions } from '../helpers'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import { calculateAge } from '../../../helpers/helpers'

const GeneralInformationSection = ({
  userRole,
  values,
  editModeList,
  buildings,
  buildingCode,
  isSubmitting,
  isPMSIntegrated,
  touched,
  errors,
  handleChange,
  setFieldTouched,
  setFieldValue,
  clearField,
  setEditModeList,
  isDisabled,
  selectBuilding,
  newClientData,
}) => {
  const { t } = useTranslation(['clients-legacy', 'clients', 'common'])
  const { realPageId, entrataId, yardiId, rentManagerId } = newClientData
  const [filters, setFilters] = useState(null)
  const [d2dMode, setD2dMode] = useState(null)
  const buildingOptions = useMemo(
    () => (filters ? getPropertyOptions(filters.buildingNumber) : []),
    [filters],
  )

  const floorOptions = useMemo(() => (filters ? getPropertyOptions(filters.floor) : []), [filters])

  const selectedBuilding = useMemo(() => {
    return buildings.find((building) => building.code === buildingCode)
  }, [buildings, buildingCode])

  const selectedBuildingTimezone = selectedBuilding?.timezone

  const getBuildingConfiguration = (building) => {
    if (building.entrataConfig) return <div className="building-configuration">E</div>
    if (building.realPageConfig) return <div className="building-configuration">RP</div>
    if (building.yardiConfig) return <div className="building-configuration">Y</div>
  }

  useEffect(() => {
    API.getClientFieldsByBuildingId(newClientData.buildingId.id).then((response) =>
      setFilters(response.data),
    )
    API.getPackagesSettings(newClientData.buildingId.id).then((response) =>
      setD2dMode(response.data.d2dMode),
    )
  }, [])

  // Door-to-Door is only truly active when it's both allowed at the Admin level
  // (selectedBuilding.packagesD2DAvailable) and turned on for this building's packages (d2dMode) -
  // see GeneralTab, where packagesD2DAvailable gates whether d2dMode can even be toggled. In House
  // Delivery is the fallback shown whenever Door-to-Door isn't effectively available. Mirrors the
  // New Design rule in general-information.tsx.
  const isDoorToDoorEffective = Boolean(selectedBuilding?.packagesD2DAvailable) && Boolean(d2dMode)

  // Note: Modal in new standards is used here. This is because almost same modal for parking spots that is used in the parking management section is used here. The parking management feature is fully built with the new standards.
  const handleOpenResidentVehiclesModal = () => {
    popupFactory.open(ResidentVehiclesModal, {
      residentId: newClientData.id,
    })
  }

  return (
    <div className={`general-information ${editModeList.length ? 'in-edit-mode' : ''}`}>
      <div className="title">{t('clients:generalInformation.title')}</div>
      <div className="information-element">
        <div className="element-name">{t('clients-legacy:newClientForm.addressPlaceholder')}</div>
        {editModeList.includes('street') ? (
          <div className="columns">
            <div className="column">
              <TextField
                name={'street'}
                placeholder={t('clients-legacy:newClientForm.addressPlaceholder')}
                value={values.street}
                isError={touched.street && !!errors.street}
                errorLabel={touched.street && errors.street}
                onChange={handleChange}
                onBlur={() => setFieldTouched('street')}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <span className="cross-icon" onClick={() => clearField('street')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() => setFieldValue('street', newClientData.street)}
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">{values.street}</div>
            {(!isDisabled || isSubmitting) && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'street'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">{t('clients-legacy:newClientForm.apartmentPlaceholder')}</div>
        {editModeList.includes('apartment') ? (
          <div className="columns">
            <div className="column">
              <TextField
                name={'apartment'}
                placeholder={t('clients-legacy:newClientForm.apartmentPlaceholder')}
                value={values.apartment}
                isError={touched.apartment && !!errors.apartment}
                errorLabel={touched.apartment && errors.apartment}
                onChange={handleChange}
                onBlur={() => setFieldTouched('apartment')}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <span className="cross-icon" onClick={() => clearField('apartment')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() => setFieldValue('apartment', newClientData.apartment)}
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">{values.apartment}</div>
            {(!isDisabled || isSubmitting) && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'apartment'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">
          {t('clients-legacy:profileView.generalInformation.dateOfBirth')}
        </div>
        {editModeList.includes('birth') ? (
          <div className="columns">
            <div className="column">
              <DateField
                name="birth"
                placeholder={t('clients-legacy:profileView.generalInformation.dateOfBirth')}
                value={values.birth}
                isError={touched.birth && !!errors.birth}
                errorLabel={touched.birth && errors.birth}
                onChange={handleChange}
                onBlur={() => setFieldTouched('birth')}
                disabled={isSubmitting}
                minDate={new Date(1900, 0, 1)}
                maxDate={new Date()}
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
              />
              <span className="cross-icon" onClick={() => clearField('birth')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() =>
                setFieldValue(
                  'birth',
                  newClientData.birth
                    ? toZonedTime(newClientData.birth, selectedBuildingTimezone)
                    : null,
                )
              }
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">
              {newClientData.birth
                ? t('clients-legacy:profileView.generalInformation.dateOfBirthWithAge', {
                    age: calculateAge(newClientData.birth),
                    date: formatInTimeZone(
                      newClientData.birth,
                      selectedBuildingTimezone,
                      'MM/dd/yyyy',
                    ),
                  })
                : '-'}
            </div>
            {(!isDisabled || isSubmitting) && !isPMSIntegrated && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'birth'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">
          {t('clients-legacy:profileView.generalInformation.parkingSpotInformation')}
        </div>
        {editModeList.includes('parkingSpotInfo') ? (
          <div className="columns">
            <div className="column">
              <TextField
                name={'parkingSpotInfo'}
                placeholder={t(
                  'clients-legacy:profileView.generalInformation.parkingSpotInformation',
                )}
                value={values.parkingSpotInfo}
                isError={touched.parkingSpotInfo && !!errors.parkingSpotInfo}
                errorLabel={touched.parkingSpotInfo && errors.parkingSpotInfo}
                onChange={handleChange}
                onBlur={() => setFieldTouched('parkingSpotInfo')}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <span className="cross-icon" onClick={() => clearField('parkingSpotInfo')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() => setFieldValue('parkingSpotInfo', newClientData.parkingSpotInfo)}
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">{values.parkingSpotInfo}</div>
            {(!isDisabled || isSubmitting) && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'parkingSpotInfo'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">{t('clients-legacy:newClientForm.zipCodePlaceholder')}</div>
        {editModeList.includes('zipCode') ? (
          <div className="columns">
            <div className="column">
              <TextField
                name="zipCode"
                type="string"
                placeholder={t('clients-legacy:newClientForm.zipCodePlaceholder')}
                value={values.zipCode}
                isError={touched.zipCode && !!errors.zipCode}
                errorLabel={touched.zipCode && errors.zipCode}
                onChange={handleChange}
                onBlur={() => setFieldTouched('zipCode')}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <span className="cross-icon" onClick={() => clearField('zipCode')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() => setFieldValue('zipCode', newClientData.zipCode)}
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">{values.zipCode}</div>
            {(!isDisabled || isSubmitting) && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'zipCode'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">{t('clients:list.columns.status')}</div>
        <div className="element-value">
          {newClientData.statusPMS ? newClientData.statusPMS.status : '-'}
        </div>
      </div>
      <div className="information-element">
        <div className="element-name">
          {t('clients-legacy:profileView.generalInformation.moveInDate')}
        </div>
        {editModeList.includes('moveInDate') ? (
          <div className="columns">
            <div className="column">
              <DateField
                name="moveInDate"
                placeholder={t(
                  'clients-legacy:profileView.generalInformation.moveInDatePlaceholder',
                )}
                value={values.moveInDate}
                isError={touched.moveInDate && !!errors.moveInDate}
                errorLabel={touched.moveInDate && errors.moveInDate}
                onChange={handleChange}
                onBlur={() => setFieldTouched('moveInDate')}
                disabled={isSubmitting}
              />
              <span className="cross-icon" onClick={() => clearField('moveInDate')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() =>
                setFieldValue(
                  'moveInDate',
                  newClientData.moveInDate
                    ? toZonedTime(newClientData.moveInDate, selectedBuildingTimezone)
                    : null,
                )
              }
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">
              {newClientData.moveInDate
                ? formatInTimeZone(newClientData.moveInDate, selectedBuildingTimezone, 'MM/dd/yyyy')
                : '-'}
            </div>
            {(!isDisabled || isSubmitting) && !isPMSIntegrated && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'moveInDate'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">
          {t('clients-legacy:profileView.generalInformation.moveOutDate')}
        </div>
        {editModeList.includes('moveOutDate') ? (
          <div className="columns">
            <div className="column">
              <DateField
                name="moveOutDate"
                placeholder={t(
                  'clients-legacy:profileView.generalInformation.moveOutDatePlaceholder',
                )}
                value={values.moveOutDate}
                isError={touched.moveOutDate && !!errors.moveOutDate}
                errorLabel={touched.moveOutDate && errors.moveOutDate}
                onChange={handleChange}
                onBlur={() => setFieldTouched('moveOutDate')}
                disabled={isSubmitting}
              />
              <span className="cross-icon" onClick={() => clearField('moveOutDate')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() =>
                setFieldValue(
                  'moveOutDate',
                  newClientData.moveOutDate
                    ? toZonedTime(newClientData.moveOutDate, selectedBuildingTimezone)
                    : null,
                )
              }
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">
              {newClientData.moveOutDate
                ? formatInTimeZone(
                    newClientData.moveOutDate,
                    selectedBuildingTimezone,
                    'MM/dd/yyyy',
                  )
                : '-'}
            </div>
            {(!isDisabled || isSubmitting) && !isPMSIntegrated && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'moveOutDate'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">
          {t('clients-legacy:profileView.generalInformation.leaseStart')}
        </div>
        {editModeList.includes('leaseStart') ? (
          <div className="columns">
            <div className="column">
              <DateField
                name="leaseStart"
                placeholder={t(
                  'clients-legacy:profileView.generalInformation.leaseStartPlaceholder',
                )}
                value={values.leaseStart}
                isError={touched.leaseStart && !!errors.leaseStart}
                errorLabel={touched.leaseStart && errors.leaseStart}
                onChange={handleChange}
                onBlur={() => setFieldTouched('leaseStart')}
                disabled={isSubmitting}
              />
              <span className="cross-icon" onClick={() => clearField('leaseStart')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() =>
                setFieldValue(
                  'leaseStart',
                  newClientData.leaseStart
                    ? toZonedTime(newClientData.leaseStart, selectedBuildingTimezone)
                    : null,
                )
              }
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">
              {newClientData.leaseStart
                ? formatInTimeZone(newClientData.leaseStart, selectedBuildingTimezone, 'MM/dd/yyyy')
                : '-'}
            </div>
            {(!isDisabled || isSubmitting) && !isPMSIntegrated && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'leaseStart'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">
          {t('clients-legacy:profileView.generalInformation.leaseEnd')}
        </div>
        {editModeList.includes('leaseEnd') ? (
          <div className="columns">
            <div className="column">
              <DateField
                name="leaseEnd"
                placeholder={t('clients-legacy:profileView.generalInformation.leaseEndPlaceholder')}
                value={values.leaseEnd}
                isError={touched.leaseEnd && !!errors.leaseEnd}
                errorLabel={touched.leaseEnd && errors.leaseEnd}
                onChange={handleChange}
                onBlur={() => setFieldTouched('leaseEnd')}
                disabled={isSubmitting}
              />
              <span className="cross-icon" onClick={() => clearField('leaseEnd')}>
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
            <span
              className="refresh-icon"
              onClick={() =>
                setFieldValue(
                  'leaseEnd',
                  newClientData.leaseEnd
                    ? toZonedTime(newClientData.leaseEnd, selectedBuildingTimezone)
                    : null,
                )
              }
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">
              {newClientData.leaseEnd
                ? formatInTimeZone(newClientData.leaseEnd, selectedBuildingTimezone, 'MM/dd/yyyy')
                : '-'}
            </div>
            {(!isDisabled || isSubmitting) && !isPMSIntegrated && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'leaseEnd'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      {!isDoorToDoorEffective && (
        <div className="information-element">
          <div className="element-name">
            {t('clients-legacy:profileView.generalInformation.inHouseDelivery')}
          </div>
          <div className="columns">
            <div className="column">
              <ToggleField
                name={'inHouse'}
                value={values.inHouse}
                onChange={(e) => setFieldValue('inHouse', e.target.checked)}
                onClick={() =>
                  setEditModeList((prev) =>
                    prev.includes('inHouse') ? prev : [...prev, 'inHouse'],
                  )
                }
              />
            </div>
          </div>
        </div>
      )}
      {['Administrator', 'Manager'].includes(userRole) && (
        <div className="information-element">
          <div className="element-name">
            {t('clients-legacy:profileView.generalInformation.changeBuilding')}
          </div>
          <div className="columns">
            <div className="column">
              <Filter
                placeholder={
                  selectedBuilding
                    ? selectedBuilding?.text
                    : t('clients:generalInformation.buildingId.placeholder')
                }
                options={buildings}
                disabled={!buildings || buildings.length <= 1 || isSubmitting}
                value={selectedBuilding ? [selectedBuilding] : []}
                name={'Building'}
                additionalClass={'filter'}
                variant={'rounded'}
                config={{ hasSearch: true }}
                closeAfterSelect={true}
                onChange={(e) => {
                  selectBuilding(e.target.value)
                  setFieldValue('code', e.target.value.code)
                  setEditModeList([...editModeList, 'code'])
                }}
              />
            </div>
            {selectedBuilding ? getBuildingConfiguration(selectedBuilding) : null}
            <span
              className="refresh-icon"
              onClick={() => {
                setFieldValue('code', newClientData.buildingId.code)
                selectBuilding(newClientData.buildingId)
              }}
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        </div>
      )}
      <div className="information-element">
        <div className="element-name">{t('clients-legacy:table.columns.buildingName')}</div>
        <div className="element-value">{newClientData.buildingId.name}</div>
      </div>
      <div className="information-element">
        <div className="element-name">
          {t('clients-legacy:profileView.generalInformation.buildingCode')}
        </div>
        <div className="element-value">{newClientData.buildingId.code}</div>
      </div>
      <div className="information-element">
        <div className="element-name">{t('clients:generalInformation.buildingNumber.label')}</div>
        {editModeList.includes('buildingNumber') ? (
          <div className="columns">
            <div className="column">
              <SelectField
                name={'buildingNumber'}
                value={values.buildingNumber}
                placeholder={t(
                  'clients-legacy:profileView.generalInformation.buildingNumberPlaceholder',
                )}
                options={buildingOptions}
                onChange={handleChange}
                placeholderStyle={{ color: '#777777' }}
                disabled={isSubmitting}
              />
            </div>
            <span
              className="refresh-icon"
              onClick={() => setFieldValue('buildingNumber', newClientData.buildingNumber)}
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">{newClientData.buildingNumber || '-'}</div>
            {(!isDisabled || isSubmitting) && Boolean(buildingOptions.length) && (
              <span
                className="editPen-icon"
                onClick={() => setEditModeList([...editModeList, 'buildingNumber'])}
              >
                <Icon icon="editPen" viewBox="2 2 20 20" />
              </span>
            )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">{t('clients:generalInformation.floor.label')}</div>
        {editModeList.includes('floor') ? (
          <div className="columns">
            <div className="column">
              <SelectField
                name={'floor'}
                value={values.floor}
                placeholder={t('clients-legacy:profileView.generalInformation.floorPlaceholder')}
                options={floorOptions}
                onChange={handleChange}
                placeholderStyle={{ color: '#777777' }}
                disabled={isSubmitting}
              />
            </div>
            <span
              className="refresh-icon"
              onClick={() => setFieldValue('floor', newClientData.floor)}
            >
              <Icon icon="refresh" viewBox="0 0 23 20" />
            </span>
          </div>
        ) : (
          <React.Fragment>
            <div className="element-value">{newClientData.floor || '-'}</div>
            {(!isDisabled || isSubmitting) &&
              Boolean(buildingOptions.length && floorOptions.length) && (
                <span
                  className="editPen-icon"
                  onClick={() => setEditModeList([...editModeList, 'floor'])}
                >
                  <Icon icon="editPen" viewBox="2 2 20 20" />
                </span>
              )}
          </React.Fragment>
        )}
      </div>
      <div className="information-element">
        <div className="element-name">{t('clients:generalInformation.pets.label')}</div>
        <div className="element-value">
          {newClientData.pets
            ? t('common:booleans.yes')
            : [null, undefined].includes(newClientData.pets)
              ? '-'
              : t('common:booleans.no')}
        </div>
      </div>
      <div className="information-element">
        <div className="element-name">
          {t('clients-legacy:profileView.generalInformation.vehicleLabel')}
        </div>
        <div className="element-value">
          <button className="button button-secondary" onClick={handleOpenResidentVehiclesModal}>
            <GearIcon size={24} /> {t('clients:generalInformation.vehicles.manage')}
          </button>
        </div>
      </div>
      {realPageId && (
        <div className="information-element">
          <div className="element-name">
            {t('clients-legacy:profileView.generalInformation.realPageId')}
          </div>
          <div className="element-value">{realPageId}</div>
        </div>
      )}
      {entrataId && (
        <div className="information-element">
          <div className="element-name">
            {t('clients-legacy:profileView.generalInformation.entrataId')}
          </div>
          <div className="element-value">{entrataId}</div>
        </div>
      )}
      {yardiId && (
        <div className="information-element">
          <div className="element-name">
            {t('clients-legacy:profileView.generalInformation.yardiId')}
          </div>
          <div className="element-value">{yardiId}</div>
        </div>
      )}
      {rentManagerId && (
        <div className="information-element">
          <div className="element-name">
            {t('clients-legacy:profileView.generalInformation.rentManagerId')}
          </div>
          <div className="element-value">{rentManagerId}</div>
        </div>
      )}
      {selectedBuilding?.appUserStatusAvailable && (
        <div className="information-element">
          <div className="element-name">
            {t('clients-legacy:profileView.generalInformation.lastAppLogin')}
          </div>
          <div className="element-value">{formatDateLong(newClientData.lastAppLogin)}</div>
        </div>
      )}
    </div>
  )
}

GeneralInformationSection.propTypes = {
  userRole: PropTypes.string,
  values: PropTypes.object,
  editModeList: PropTypes.array,
  buildings: PropTypes.array,
  buildingCode: PropTypes.number,
  isSubmitting: PropTypes.bool,
  isPMSIntegrated: PropTypes.bool,
  isDisabled: PropTypes.bool,
  touched: PropTypes.object,
  errors: PropTypes.object,
  newClientData: PropTypes.object,
  handleChange: PropTypes.func,
  setFieldTouched: PropTypes.func,
  setFieldValue: PropTypes.func,
  clearField: PropTypes.func,
  setEditModeList: PropTypes.func,
  selectBuilding: PropTypes.func,
}

export default GeneralInformationSection
